/**
 * KONTRAKTY ZDARZEŃ MIĘDZYORGANOWYCH — słownik żywych styków magistrali.
 *
 * Zdarzenie trafia tutaj, gdy KONSUMUJE je inny organ niż producent. Wpis nie
 * jest deklaracją intencji, tylko twierdzeniem o kodzie: każdy `dowod` to
 * plik + token, którego obecność sprawdza test kontraktowy
 * (`tests/unit/packages/kontrakt-zdarzen.test.ts`) — usunięcie producenta
 * albo konsumenta bez aktualizacji słownika czerwieni CI, tak samo jak nowy
 * klucz w dyspozytorze konsumenta bez wpisu tutaj.
 *
 * `ZNANE_ROZJAZDY` to jawna lista styków zepsutych DZIŚ (zmierzone przez
 * sztab 2026-08-18), każdy z numerem wątku naprawy z
 * `docs/02-ARCHITECTURE/PLAN_REALIZACJI_ORGANIZM_FABRICA.md`. Wpis schodzi
 * z listy w PR-ze, który styk naprawia — a wtedy test zaczyna wymagać od
 * przykładowego ładunku kompletu pól konsumenta.
 *
 * Magistrala zamówień (PR 1.8, `docs/16-ZAMOWIENIA/02-MAGISTRALA-NATS.md` §5):
 * `KONTRAKTY_KOMEND` (komendy `ORGAN_COMMANDS`) i `SZKIELETY_ZDARZEN`
 * (zdarzenia rodzin outboxu, które nikt jeszcze nie emituje) — oba na dole
 * pliku, klucze wyłącznie ze słownika w `nats.ts`.
 */

import {
  KONSUMENCI_KOMEND,
  type KluczKomendy,
  type KluczZdarzeniaMagistrali,
  type OrganKomend,
} from './nats.js';

/** Dowód w kodzie: plik (ścieżka od korzenia repo) musi zawierać token. */
export interface DowodKontraktu {
  plik: string;
  token: string;
}

export interface KontraktZdarzenia {
  /** Po ludzku: co ten styk robi w organizmie. */
  opis: string;
  /** Producenci — dowody publikacji. Pusta lista = zdarzenie bez emitenta. */
  producenci: DowodKontraktu[];
  /** Konsumenci w INNYM organie — dowody konsumpcji. */
  konsumenci: DowodKontraktu[];
  /**
   * Pola ładunku, które konsument realnie czyta (token pola musi występować
   * w pliku któregoś konsumenta — test to sprawdza). Lista może być węższa
   * niż pełny ładunek; nie może kłamać.
   */
  polaKonsumenta: string[];
  /** Przykładowy ładunek producenta — musi nieść `polaKonsumenta`. */
  przykladowyLadunek: Record<string, unknown>;
}

/** Klucz = `{module}.{event}` — dokładnie segmenty 4 i 5 subjectu EVENTS. */
export const KONTRAKTY_ZDARZEN: Record<string, KontraktZdarzenia> = {
  'fabrica.ingredient_consumed': {
    opis:
      'Zużycie surowca w produkcji → dekrementacja stanu Provisio przez durable ' +
      'konsumenta w nexus-core (idempotencja po source_event_id, migracja 264).',
    producenci: [
      { plik: 'services/fabrica-core/src/nats/events.ts', token: "'ingredient_consumed'" },
    ],
    konsumenci: [
      {
        plik: 'services/nexus-core/src/provisio/provisioStockCore.ts',
        token: 'INGREDIENT_CONSUMED_FILTER',
      },
      { plik: 'apps/module-provisio/src/lib/stock-consumption.ts', token: 'stock_item_id' },
    ],
    polaKonsumenta: ['stock_item_id', 'quantity', 'production_order_line_id'],
    przykladowyLadunek: {
      stock_item_id: '00000000-0000-0000-0000-000000000001',
      quantity: 12.5,
      production_order_line_id: '00000000-0000-0000-0000-000000000002',
      event_id: '00000000-0000-0000-0000-000000000003',
    },
  },

  'agenda.task_created': {
    opis:
      'Zadanie z etapów zleceń / awarii jakości / alarmów sprzętu → ' +
      'AgendaTaskMaterializer w nexus-core pisze agenda_tasks (UNIQUE z 168). ' +
      'Dwa kształty na jednym temacie: fabrica-core publikuje płasko, ' +
      'nexus-core (minutniki, Conspectus, audyt PIP LexShift) od 26.10.2026 ' +
      'i neurosense-core (działanie korygujące HACCP, plan 65 §9.3) ' +
      'w kopercie v2 (`kopertaZadaniaAgendy`); materializator czyta oba przez ' +
      'readEventPayload, więc pola ładunku są te same. Kopertę przyjmuje tylko ' +
      'od zaufanych usług (`odmowaNadawcyZadania`) — ta sama nazwa z przeglądarki ' +
      '(websocket-gateway) jest powiadomieniem, nie poleceniem.',
    producenci: [
      {
        plik: 'services/fabrica-core/src/core/CrossModuleHandler.ts',
        token: 'AGENDA_EVENTS.taskCreated',
      },
      // Działanie korygujące HACCP z alarmu temperatury i wilgotności (plan 65
      // §9.3, L8) — w kopercie, `source` koperty `neurosense-core`.
      {
        plik: 'services/neurosense-core/src/domain/dzialanieKorygujace.ts',
        token: 'AGENDA_EVENTS.taskCreated',
      },
    ],
    konsumenci: [
      {
        plik: 'services/nexus-core/src/agenda/AgendaTaskMaterializer.ts',
        token: 'AGENDA_TASK_CREATED_FILTER',
      },
      { plik: 'services/nexus-core/src/agenda/agendaTaskCore.ts', token: 'payload.title' },
    ],
    polaKonsumenta: ['tenant_id', 'title', 'source', 'source_reference'],
    przykladowyLadunek: {
      tenant_id: 'tenant-1',
      title: 'Kontrola jakości: etap CIASTO',
      source: 'fabrica',
      source_reference: 'stage:00000000-0000-0000-0000-000000000004',
      priority: 'high',
    },
  },

  'provisio.stock_received': {
    opis: 'Przyjęcie dostawy → fabrica-core czyści braki materiałowe.',
    producenci: [
      { plik: 'apps/module-provisio/src/hooks/use-provisio.ts', token: "'stock_received'" },
    ],
    konsumenci: [
      { plik: 'services/fabrica-core/src/index.ts', token: "includes('stock_received')" },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { inventory_id: '00000000-0000-0000-0000-000000000005', quantity: 100 },
  },

  'provisio.low_stock_alert': {
    opis: 'Niski stan → braki materiałowe w planerze fabrica-core.',
    producenci: [
      { plik: 'apps/module-provisio/src/lib/provisio-events.ts', token: "'low_stock_alert'" },
    ],
    konsumenci: [
      { plik: 'services/fabrica-core/src/index.ts', token: "includes('low_stock_alert')" },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { inventory_id: '00000000-0000-0000-0000-000000000005', quantity: 2 },
  },

  'provisio.out_of_stock_alert': {
    opis: 'Brak stanu → braki materiałowe w planerze fabrica-core.',
    producenci: [
      { plik: 'apps/module-provisio/src/lib/provisio-events.ts', token: "'out_of_stock_alert'" },
    ],
    konsumenci: [
      { plik: 'services/fabrica-core/src/index.ts', token: "includes('out_of_stock_alert')" },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { inventory_id: '00000000-0000-0000-0000-000000000005', quantity: 0 },
  },

  'provisio.mrp_proposal_generated': {
    opis:
      'Propozycja MRP → `fabrica_material_availability` (635). UWAGA NA POLE ' +
      '`needed_by`: producent wpisuje tam `dzisiaj + lead_time_days`, czyli ' +
      'moment DOSTAWY, a nie termin potrzeby. Konsument mapuje je jawnie na ' +
      'kolumnę `dostepne_od` — czytanie tego jako terminu ustawiałoby produkcję ' +
      'przed przyjazdem surowca.',
    producenci: [
      { plik: 'apps/module-provisio/src/hooks/use-provisio.ts', token: "'mrp_proposal_generated'" },
      { plik: 'apps/module-provisio/src/lib/provisio-events.ts', token: 'buildMrpProposalPayload' },
    ],
    konsumenci: [
      { plik: 'services/fabrica-core/src/index.ts', token: "includes('mrp_proposal_generated')" },
      {
        plik: 'services/fabrica-core/src/core/dostepnoscSurowca.ts',
        token: 'materializujPropozycje',
      },
    ],
    polaKonsumenta: ['proposal_id', 'generated_at', 'items'],
    przykladowyLadunek: {
      proposal_id: 'mrp-00000000-0000-0000-0000-000000000001-2026-08-26T05:00:00.000Z',
      generated_at: '2026-08-26T05:00:00.000Z',
      items: [
        {
          stock_item_id: '00000000-0000-0000-0000-000000000005',
          quantity: 120,
          unit: 'kg',
          // Data DOSTĘPNOŚCI mimo nazwy — patrz `opis`.
          needed_by: '2026-08-29T05:00:00.000Z',
        },
      ],
    },
  },

  'lexshift.employee_clocked_in': {
    opis:
      'Odbicie wejścia → oś czasu stanowiska (worker_assigned) i punkty Aegis. ' +
      'W14 naprawił trzy rozjazdy naraz: koperta v2 jest spłaszczana w gałęzi ' +
      'lexshift, konsument czyta `worker_id` (nazwę, którą producent NAPRAWDĘ ' +
      'wysyła), a stanowisko wychodzi z `device_id` przez ' +
      '`fabrica_workstations.hardware_device_id`. Gdy urządzenia nie da się ' +
      'przypisać do dokładnie jednego stanowiska, konsument ODMAWIA po imieniu ' +
      'zamiast wybierać pierwsze z brzegu. JEDEN TOR PRODUKCJI, NIE DWA: ' +
      'wyzwalacz `trg_lexshift_time_entry_outbox` (migracja 616) wkłada KAŻDE ' +
      'odbicie — z kiosku i z panelu — do `lexshift_event_outbox`, a wysyła je ' +
      'drainer bramy. Panel publikował to zdarzenie także sam; przestał, bo ' +
      'jeden fakt jechał wtedy w dwóch kopertach o dwóch `message_id`, a Aegis ' +
      'dedupuje właśnie po `message_id` — czyli płacił dwa razy. Kiosk jest ' +
      'zarazem JEDYNYM torem, który wypełnia `device_id`.',
    producenci: [
      {
        plik: 'services/websocket-gateway/src/skrzynkaNadawcza.ts',
        token: "'lexshift_event_outbox'",
      },
    ],
    konsumenci: [
      { plik: 'services/fabrica-core/src/index.ts', token: "includes('employee_clocked_in')" },
      { plik: 'services/fabrica-core/src/core/odbicieObecnosci.ts', token: "'worker_id'" },
      {
        plik: 'services/fabrica-core/src/core/CrossModuleHandler.ts',
        token: 'stanowiskoZUrzadzenia',
      },
      {
        plik: 'services/neurosense-core/src/nats/gamificationHandler.ts',
        token: "'lexshift.employee_clocked_in'",
      },
    ],
    polaKonsumenta: ['worker_id', 'device_id'],
    przykladowyLadunek: {
      entry_id: '00000000-0000-0000-0000-000000000007',
      worker_id: '00000000-0000-0000-0000-000000000006',
      entry_type: 'clock_in',
      device_id: null,
      device_type: 'kiosk',
      recorded_at: '2026-08-20T04:00:00.000Z',
    },
  },

  'lexshift.employee_clocked_out': {
    opis:
      'Odbicie wyjścia → worker_unassigned na osi stanowiska. Ta sama droga co ' +
      'wejście i ten sam jedyny tor produkcji: wyzwalacz z migracji 616 → ' +
      '`lexshift_event_outbox` → drainer bramy.',
    producenci: [
      {
        plik: 'services/websocket-gateway/src/skrzynkaNadawcza.ts',
        token: "'lexshift_event_outbox'",
      },
    ],
    konsumenci: [
      { plik: 'services/fabrica-core/src/index.ts', token: "includes('employee_clocked_out')" },
      { plik: 'services/fabrica-core/src/core/odbicieObecnosci.ts', token: "'employee_id'" },
      {
        plik: 'services/fabrica-core/src/core/CrossModuleHandler.ts',
        token: "kierunek === 'in' ? 'worker_assigned' : 'worker_unassigned'",
      },
    ],
    polaKonsumenta: ['worker_id', 'device_id'],
    przykladowyLadunek: {
      entry_id: '00000000-0000-0000-0000-000000000008',
      worker_id: '00000000-0000-0000-0000-000000000006',
      entry_type: 'clock_out',
      device_id: null,
      device_type: 'kiosk',
      recorded_at: '2026-08-20T12:00:00.000Z',
    },
  },

  'lexshift.schedule_published': {
    opis:
      'Publikacja grafiku → konflikty obsady wobec zleceń z okna grafiku. ' +
      'Konsument liczy je z widoku `lexshift_dostepnosc_produkcji` (migracja ' +
      '613), emituje `fabrica.schedule_conflict_detected` i zakłada sprawę ' +
      '`obsada_grafiku` w Skrzynce Decyzji. Nie przesuwa ani jednego zlecenia.',
    producenci: [
      { plik: 'apps/module-lexshift/src/hooks/use-lexshift.ts', token: "'schedule_published'" },
    ],
    konsumenci: [
      { plik: 'services/fabrica-core/src/index.ts', token: "includes('schedule_published')" },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { week_start: '2026-08-17' },
  },

  'neurosense.sensor_alert': {
    opis:
      'Alert czujnika → alarm stanowiska przez hardware_device_id → oś czasu ' +
      '+ zadanie Agendy (mapowanie urządzeń naprawia wątek W14, migracja 544).',
    producenci: [
      { plik: 'services/neurosense-core/src/nats/publikatorProgu.ts', token: 'sensorAlert' },
    ],
    konsumenci: [{ plik: 'services/fabrica-core/src/index.ts', token: "includes('sensor_alert')" }],
    polaKonsumenta: [],
    przykladowyLadunek: { device_id: 'W-100-60-5g', alert_type: 'high_temperature' },
  },

  'neurosense.sensor_offline': {
    opis: 'Czujnik zamilkł → alarm stanowiska (ta sama trasa co sensor_alert).',
    producenci: [
      {
        plik: 'services/neurosense-core/src/domain/offlineDetectionService.ts',
        token: 'sensorOffline',
      },
    ],
    konsumenci: [
      { plik: 'services/fabrica-core/src/index.ts', token: "includes('sensor_offline')" },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { device_id: 'W-100-60-5g' },
  },

  'fabrica.production_order_completed': {
    opis: 'Zlecenie ukończone → punkty Aegis + e-mail (pocztaZdarzen).',
    producenci: [
      { plik: 'services/fabrica-core/src/nats/events.ts', token: "'production_order_completed'" },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/nats/gamificationHandler.ts',
        token: "'fabrica.production_order_completed'",
      },
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'fabrica.production_order_completed'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { order_id: '00000000-0000-0000-0000-000000000007' },
  },

  'fabrica.stage_completed': {
    opis: 'Etap ukończony → punkty Aegis (task_complete).',
    producenci: [{ plik: 'services/fabrica-core/src/nats/events.ts', token: "'stage_completed'" }],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/nats/gamificationHandler.ts',
        token: "'fabrica.stage_completed'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { stage_id: '00000000-0000-0000-0000-000000000008' },
  },

  'fabrica.batch_completed': {
    opis: 'Partia wyrobu ukończona → punkty Aegis (batch_complete).',
    producenci: [{ plik: 'services/fabrica-core/src/nats/events.ts', token: "'batch_completed'" }],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/nats/gamificationHandler.ts',
        token: "'fabrica.batch_completed'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { batch_id: '00000000-0000-0000-0000-000000000009' },
  },

  'fabrica.quality_check_passed': {
    opis:
      'Kontrola jakości OK → punkty Aegis. DWIE drogi, dwa rodzaje podpisu (W15): ' +
      'werdykt naważenia niesie `worker_id` (consumed_by, klucz obcy do ' +
      'shop_floor_workers), a kontrola z przeglądarki `user_id` (konto z tokenu). ' +
      '`resolveWorkerId` przyjmuje oba i tylko oba — do W15 nie było ANI JEDNEGO, ' +
      'więc reguła „Kontrola jakości +15 pkt" nie naliczyła nic od powstania.',
    producenci: [
      { plik: 'services/fabrica-core/src/nats/events.ts', token: "'quality_check_passed'" },
      { plik: 'services/fabrica-core/src/index.ts', token: 'qualityCheckPassed' },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/nats/gamificationHandler.ts',
        token: "'fabrica.quality_check_passed'",
      },
    ],
    // Deklarujemy `worker_id`, bo to jest podpis drogi, która na tej platformie
    // zdarza się naprawdę często (naważenie na hali). `user_id` jest drugim
    // legalnym wejściem tej samej funkcji i przychodzi z przeglądarki —
    // przykładowy ładunek nie może nieść obu naraz, bo żadne zdarzenie ich nie
    // niesie: człowiek przy wadze i człowiek przy ekranie to dwa różne rejestry.
    polaKonsumenta: ['worker_id'],
    przykladowyLadunek: {
      stage_id: '00000000-0000-0000-0000-000000000008',
      check_type: 'weighing',
      worker_id: '00000000-0000-0000-0000-00000000000a',
      measured_value: 10.02,
      min_value: 9.9,
      max_value: 10.1,
      unit: 'kg',
    },
  },

  'fabrica.quality_check_failed': {
    opis:
      'Kontrola jakości NIE OK → e-mail + zadanie Agendy. Od W15 niesie tożsamość ' +
      'tą samą parą co wariant OK: `worker_id` z naważenia albo `user_id` z tokenu.',
    producenci: [
      { plik: 'services/fabrica-core/src/nats/events.ts', token: "'quality_check_failed'" },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'fabrica.quality_check_failed'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: {
      stage_id: '00000000-0000-0000-0000-000000000008',
      check_type: 'weighing',
    },
  },

  'fabrica.material_shortage': {
    opis: 'Brak surowca do produkcji → e-mail (pocztaZdarzen).',
    producenci: [
      { plik: 'services/fabrica-core/src/nats/events.ts', token: "'material_shortage'" },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'fabrica.material_shortage'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { stock_item_id: '00000000-0000-0000-0000-000000000005' },
  },

  'fabrica.sourdough_feeding_overdue': {
    opis: 'Zakwas do karmienia → e-mail (pocztaZdarzen).',
    producenci: [
      { plik: 'services/fabrica-core/src/nats/events.ts', token: "'sourdough_feeding_overdue'" },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'fabrica.sourdough_feeding_overdue'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { culture_id: '00000000-0000-0000-0000-00000000000a' },
  },

  'fabrica.equipment_alarm': {
    opis: 'Alarm sprzętu → e-mail (pocztaZdarzen).',
    producenci: [{ plik: 'services/fabrica-core/src/nats/events.ts', token: "'equipment_alarm'" }],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'fabrica.equipment_alarm'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { equipment_id: '00000000-0000-0000-0000-00000000000b' },
  },

  'fabrica.panel_fault': {
    opis:
      'Stanowisko produkcyjne przestało być sprawne → e-mail (pocztaZdarzen). ' +
      'Jedyne zdarzenie tej rodziny mówiące o NASZYM oprogramowaniu, a nie ' +
      'o maszynie klienta — skutek jest ten sam, bo stanowisko ważące na ' +
      'symulatorze produkuje zapis, którego nie da się obronić przy audycie. ' +
      'Nadawca (`core/dozorFloty.ts`) sam pilnuje powtórek: emituje przy ' +
      'ZMIANIE zestawu usterek albo raz na dobę, nigdy co przebieg.',
    producenci: [{ plik: 'services/fabrica-core/src/nats/events.ts', token: "'panel_fault'" }],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'fabrica.panel_fault'",
      },
    ],
    polaKonsumenta: [
      'license_id',
      'workstation_name',
      'usterka_glowna',
      'usterki_opis',
      'liczba_usterek',
      'cisza_opis',
      'przypomnienie',
    ],
    przykladowyLadunek: {
      license_id: '00000000-0000-0000-0000-00000000000c',
      workstation_name: 'STANOWISKO 3',
      usterka_glowna: 'waży na symulatorze — agent nie wstał albo go nie ma',
      usterki_opis: 'waży na symulatorze — agent nie wstał albo go nie ma · rozjazd zegara 15 min',
      liczba_usterek: 2,
      cisza_opis: '4 min',
      przypomnienie: false,
    },
  },

  'fabrica.calibration_due': {
    opis:
      'Świadectwo wzorcowania przyrządu wygasa albo już wygasło → e-mail ' +
      '(pocztaZdarzen). Powód jest ten sam, co przy `panel_fault`, ale sięga ' +
      'dalej niż ekran: pomiar wykonany przyrządem po terminie NIE JEST ' +
      'odrzucany (doktryna migracji `310`) — zostaje wykonany i ostemplowany ' +
      'jako niewiarygodny NA ZAWSZE, bo stempel jest zamrażany w chwili ' +
      'pomiaru. Alert jest więc jedyną rzeczą, która może temu zapobiec ' +
      'ZANIM to się stanie. Nadawca (`core/dozorWzorcowan.ts`) sam pilnuje ' +
      'powtórek: emituje przy ZMIANIE STANU albo raz na tydzień, nigdy co ' +
      'przebieg — i podpisuje stan BEZ liczby dni, bo ta maleje każdej doby.',
    producenci: [{ plik: 'services/fabrica-core/src/nats/events.ts', token: "'calibration_due'" }],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'fabrica.calibration_due'",
      },
    ],
    polaKonsumenta: [
      'instrument_id',
      'instrument_name',
      'serial_number',
      'stan_opis',
      'wazne_do',
      'przypomnienie',
    ],
    przykladowyLadunek: {
      instrument_id: '00000000-0000-0000-0000-00000000000d',
      instrument_name: 'PH-01 — Pehametr HALO2',
      serial_number: 'H06200027311',
      stan_opis: 'ważne jeszcze 12 dni',
      wazne_do: '2026-10-03T00:00:00.000Z',
      przypomnienie: false,
    },
  },

  'sprzedaz.zastoj': {
    opis:
      'Źródło sprzedaży przestało dostarczać paragony → e-mail (pocztaZdarzen). ' +
      'Drugie po `fabrica.panel_fault` zdarzenie mówiące o NASZYM oprogramowaniu, ' +
      'a nie o pracy klienta: kasa sprzedaje dalej, ale system o tym nie wie, ' +
      'więc prognoza obsady na jutro powstaje z wczorajszego obrazu i nikt tego ' +
      'nie widzi. Pomiar (`SprzedazSync.zastoje()`, próg `ZASTOJ_H = 6 h`) ' +
      'istniał od etapu B i przez dobę nie miał czytelnika — 11.09.2026 ' +
      'synchronizacja stała dziewięć godzin, rejestr wiedział od pierwszej ' +
      'minuty, nikt nie wszedł. Nadawca (`sprzedaz/dozorSprzedazy.ts`) sam ' +
      'pilnuje powtórek: emituje przy ZMIANIE zestawu milczących źródeł albo ' +
      'raz na dobę, nigdy co przebieg.',
    producenci: [
      { plik: 'services/nexus-core/src/index.ts', token: "'sprzedaz.zastoj'" },
      {
        plik: 'services/nexus-core/src/sprzedaz/dozorSprzedazy.ts',
        token: 'decydujOZastojach',
      },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'sprzedaz.zastoj'",
      },
    ],
    polaKonsumenta: [
      'kind',
      'zrodlo_nazwa',
      'cisza_opis',
      'ostatni_ok_opis',
      'prog_godzin',
      'przypomnienie',
    ],
    przykladowyLadunek: {
      kind: 'gopos',
      zrodlo_nazwa: 'kasa GoPOS',
      cisza_opis: '9 h',
      ostatni_ok_opis: '2026-09-12T01:20:00.000Z',
      prog_godzin: '6',
      przypomnienie: false,
    },
  },

  'nexus_eye.gateway_milczy': {
    opis:
      'Agent kamer Conspectusa przestał bić tętnem, a ma włączone kamery → ' +
      'e-mail (pocztaZdarzen). Poziom gatewaya, nie kamery: gdy milczy agent, ' +
      'milczą naraz wszystkie jego kamery, a ich `ostatni_blad` zostaje pusty, ' +
      'bo zgłasza go właśnie agent. 22.09.2026 agent OGYY skasował parowanie ' +
      'po `401` z czasu awarii bazy i trzy kamery milczały jedenaście dni — ' +
      '`last_seen_at` wiedział od pierwszej minuty, nikt nie wszedł. Nadawca ' +
      '(`nexus-eye/dozorGatewayow.ts`) sam pilnuje powtórek: emituje przy ' +
      'wejściu w ciszę (próg godzina) albo raz na dobę, nigdy co przebieg.',
    producenci: [
      { plik: 'services/nexus-core/src/index.ts', token: "'nexus_eye.gateway_milczy'" },
      {
        plik: 'services/nexus-core/src/nexus-eye/dozorGatewayow.ts',
        token: 'decydujOGatewayach',
      },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'nexus_eye.gateway_milczy'",
      },
    ],
    polaKonsumenta: [
      'gateway_id',
      'host_nazwa',
      'gateway_nazwa',
      'kamery_opis',
      'liczba_kamer',
      'cisza_opis',
      'ostatni_kontakt',
      'wersja_agenta',
      'przypomnienie',
    ],
    przykladowyLadunek: {
      gateway_id: 'e8453539-431b-4177-930a-69d1bc6fefab',
      gateway_nazwa: 'Gateway 2026-08-04 06:18',
      host_nazwa: 'RECEPTOR_BRAIN',
      wersja_agenta: '0.1.3',
      kamery_opis: 'CIASTA · STÓŁ-1-MIESIARKA · WYSPA',
      liczba_kamer: 3,
      cisza_opis: '2 h',
      ostatni_kontakt: '2026-09-22T02:07:44.837Z',
      prog_minut: '60',
      przypomnienie: false,
      occurred_at: '2026-09-22T04:15:00.000Z',
    },
  },

  'fabrica.label_printed': {
    opis:
      'Etykieta wydrukowana i WIADOMO CZEGO — most W12. Emituje print-core przy ' +
      'zamknięciu zadania druku, ale WYŁĄCZNIE gdy zadanie ma `slad` (kolumna ' +
      'z 550): faktura i list przewozowy milczą. Ładunek niesie identyfikatory ' +
      'płasko, bo konsument fabrica czyta po nazwach kolumn. Konsumenta ' +
      'imiennego jeszcze nie ma — zdarzenie łapie ogólny encoder Nexusa ' +
      'z rodziny `tenant.*.events.>` i to jest cała dzisiejsza droga.',
    producenci: [
      {
        plik: 'services/print-core/src/nats/PrintPublisher.ts',
        token: "'fabrica.label_printed'",
      },
      { plik: 'services/print-core/src/index.ts', token: 'publishLabelPrinted' },
    ],
    konsumenci: [],
    polaKonsumenta: [],
    przykladowyLadunek: {
      production_order_line_id: '00000000-0000-0000-0000-00000000000c',
      job_id: '00000000-0000-0000-0000-00000000000d',
      document_type: 'etykieta_wyrobu',
      copies: 1,
    },
  },

  'print.failed': {
    opis:
      'Zadanie druku poległo (agent zgłosił błąd albo sweeper dead-letterował ' +
      'po wyczerpaniu prób) → e-mail `print_job_failed` (pocztaZdarzen) — kanał ' +
      'odwrotny organu druku: panel przy stole po budżecie odpytywania mówi już ' +
      'tylko „nie wiem", więc skrzynka jest jedyną drogą do człowieka, który ' +
      'może podejść do drukarki. UWAGA na kształt: to `PrintEventEnvelope` ' +
      '(services/print-core/src/envelope.ts), NIE koperta v2 — `job_id` ' +
      'i `occurred_at` leżą na wierzchu, a `error`/`agent_id` w zagnieżdżonym ' +
      '`data` (konsument schodzi tam jawnie, helper `zDanychDruku`).',
    producenci: [
      { plik: 'services/print-core/src/envelope.ts', token: 'failed: PRINT_EVENTS.failed' },
      { plik: 'services/print-core/src/index.ts', token: "kind: 'failed'" },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'print.failed'",
      },
    ],
    polaKonsumenta: ['job_id', 'occurred_at'],
    przykladowyLadunek: {
      event_id: '00000000-0000-0000-0000-000000000010',
      event: 'failed',
      module: 'print',
      organization_id: '00000000-0000-0000-0000-000000000011',
      job_id: '00000000-0000-0000-0000-000000000012',
      occurred_at: '2026-08-24T12:00:00.000Z',
      data: { attempts: 5, error: 'stale_claim_dead_letter' },
    },
  },

  'fabrica.water_temp_suggested': {
    opis:
      'Sugestia temperatury wody dla ruszającego zarobu (W16). Liczy ją ' +
      'fabrica-core przy starcie etapu TYM SAMYM silnikiem, co telefon ' +
      '(`@insolitum/waterandice-core`, ADR-004) — dwa tory dałyby hali dwie ' +
      'prawdy o jednej dzieży. SYGNAŁ, nie nastawa: nic nie wymusza, żeby ' +
      'nalać tyle. Pada wyłącznie, gdy etap ma docelową temperaturę ciasta ' +
      'ORAZ są świeże pomiary sali i mąki — brak pomiaru kończy się ciszą, ' +
      'a nie liczbą policzoną z wymyślonej domyślnej.',
    producenci: [
      { plik: 'services/fabrica-core/src/nats/events.ts', token: "'water_temp_suggested'" },
      { plik: 'services/fabrica-core/src/index.ts', token: 'waterTempSuggested' },
    ],
    konsumenci: [],
    polaKonsumenta: [],
    przykladowyLadunek: {
      stage_id: '00000000-0000-0000-0000-00000000000e',
      woda_c: 21.6,
      zalozenia: { ddt_c: 26, sala_c: 22, maka_c: 20, tarcie_c: 2, czlonow: 3 },
    },
  },

  'fabrica.schedule_conflict_detected': {
    opis:
      'Opublikowany grafik LexShiftu nie pokrywa planu produkcji (W14). ' +
      'SYGNAŁ, nie przesunięcie: fabrica nie rusza ani jednego zlecenia — ' +
      'emituje to zdarzenie i zakłada sprawę `obsada_grafiku` w Skrzynce ' +
      'Decyzji, a przesunięcie zostaje decyzją człowieka. Dwa rozpoznawane ' +
      'konflikty: `brak_obsady` (dzień z planem, w grafiku nikogo) ' +
      'i `poza_oknem` (obsada jest, ale nie o tej godzinie).',
    producenci: [
      { plik: 'services/fabrica-core/src/nats/events.ts', token: "'schedule_conflict_detected'" },
      {
        plik: 'services/fabrica-core/src/core/CrossModuleHandler.ts',
        token: 'scheduleConflictDetected',
      },
    ],
    // Konsumenta jeszcze nie ma i to NIE jest rozjazd: producent bez konsumenta
    // jest legalny (tak stoi dziś `fabrica.water_temp_suggested`). Odbiorcą,
    // który liczy się dziś, jest Skrzynka Decyzji — a do niej sprawa idzie
    // zapisem do `nexus_sprawy`, nie magistralą. `polaKonsumenta` MUSI zostać
    // puste, dopóki lista konsumentów jest pusta: bramka sprawdza je przez
    // `konsumenci.some(...)`, co na pustej liście jest zawsze fałszem.
    konsumenci: [],
    polaKonsumenta: [],
    przykladowyLadunek: {
      week_start: '2026-08-24',
      week_end: '2026-08-31',
      konfliktow: 1,
      konflikty: [
        {
          rodzaj: 'brak_obsady',
          dzien: '2026-08-29',
          osob: 0,
          okno_od: null,
          okno_do: null,
          zlecenia: ['ZP/2026/08/141'],
        },
      ],
    },
  },

  'waterandice.temperature_recorded': {
    opis: 'Pomiar temperatury wody → punkty Aegis; w W16 także VERIFY sugestii.',
    producenci: [
      {
        plik: 'apps/module-waterandice/src/hooks/use-waterandice.ts',
        token: "'temperature_recorded'",
      },
      {
        plik: 'services/websocket-gateway/src/skrzynkaNadawcza.ts',
        token: "'waterandice_event_outbox'",
      },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/nats/gamificationHandler.ts',
        token: "'waterandice.temperature_recorded'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { measurement_id: '00000000-0000-0000-0000-00000000000c' },
  },

  'waterandice.dough_started': {
    opis: 'Ciasto rozpoczęte → punkty Aegis.',
    producenci: [
      { plik: 'apps/module-waterandice/src/hooks/use-waterandice.ts', token: "'dough_started'" },
      {
        plik: 'services/websocket-gateway/src/skrzynkaNadawcza.ts',
        token: "'waterandice_event_outbox'",
      },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/nats/gamificationHandler.ts',
        token: "'waterandice.dough_started'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { dough_id: '00000000-0000-0000-0000-00000000000d' },
  },

  'waterandice.dough_ready': {
    opis: 'Ciasto gotowe → punkty Aegis.',
    producenci: [
      { plik: 'apps/module-waterandice/src/hooks/use-waterandice.ts', token: "'dough_ready'" },
      {
        plik: 'services/websocket-gateway/src/skrzynkaNadawcza.ts',
        token: "'waterandice_event_outbox'",
      },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/nats/gamificationHandler.ts',
        token: "'waterandice.dough_ready'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { dough_id: '00000000-0000-0000-0000-00000000000d' },
  },

  'waterandice.dough_status_changed': {
    opis:
      'Zmiana statusu ciasta. Ma mapper grafu w bramie (pamięć organizmu), ale ' +
      'ZERO konsumentów logiki — punkty Aegis liczą się z `dough_started`/' +
      '`dough_ready`, nie stąd. Wpis istnieje, bo zdarzenie realnie leci ' +
      'magistralą z DWÓCH torów, a słownik ma mówić prawdę o tym, co jedzie.',
    producenci: [
      {
        plik: 'apps/module-waterandice/src/hooks/use-waterandice.ts',
        token: "'dough_status_changed'",
      },
      {
        plik: 'services/websocket-gateway/src/skrzynkaNadawcza.ts',
        token: "'waterandice_event_outbox'",
      },
    ],
    konsumenci: [],
    polaKonsumenta: [],
    przykladowyLadunek: {
      dough_id: '00000000-0000-0000-0000-00000000000d',
      status: 'used',
      batch_number: 'WI-19FFB9FD73B',
    },
  },

  'waterandice.temperature_alert': {
    opis: 'Temperatura wody poza widełkami → e-mail (pocztaZdarzen).',
    producenci: [
      {
        plik: 'apps/module-waterandice/src/hooks/use-waterandice.ts',
        token: "'temperature_alert'",
      },
      {
        plik: 'services/websocket-gateway/src/skrzynkaNadawcza.ts',
        token: "'waterandice_event_outbox'",
      },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'waterandice.temperature_alert'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { measurement_id: '00000000-0000-0000-0000-00000000000c' },
  },

  'agenda.task_completed': {
    opis: 'Zadanie Agendy ukończone → punkty Aegis (task_complete).',
    producenci: [{ plik: 'apps/module-agenda/src/hooks/use-agenda.ts', token: "'task_completed'" }],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/nats/gamificationHandler.ts',
        token: "'agenda.task_completed'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { task_id: '00000000-0000-0000-0000-00000000000e' },
  },

  'agenda.task_assigned': {
    opis:
      'Przypisanie zadania → e-mail. Wpis w mapie maili istnieje, producenta ' +
      'brak — mail nie wychodzi. Patrz ZNANE_ROZJAZDY.',
    producenci: [],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'agenda.task_assigned'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: {},
  },

  'agenda.task_overdue': {
    opis:
      'Zadanie po terminie → e-mail do wykonawcy i przełożonych. Producentem ' +
      'jest tik silnika Agendy; subject buduje SZABLONEM, nie stałą ' +
      'AGENDA_EVENTS.taskOverdue — dlatego do 19.09.2026 słownik uważał to ' +
      'zdarzenie za bezproducenckie i trzymał je w ZNANE_ROZJAZDY. Od ' +
      '26.10.2026 w kopercie v2 (`kopertaZdarzeniaAgendy`, type ' +
      '`agenda.task_overdue`); ładunek poniżej to jej `payload`, który ' +
      'odbiorcy dostają z readEventPayload pole w pole.',
    producenci: [
      {
        plik: 'services/nexus-core/src/agenda/AgendaEngine.ts',
        token: '`tenant.${organizationId}.events.agenda.${event.event}`',
      },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'agenda.task_overdue'",
      },
    ],
    // `task_name` ORAZ `title` są tu oba świadomie: mapa maili czyta pierwsze,
    // feed powłoki (`natsEventMessage.ts`) drugie. `assignee` to NAZWA, nie
    // identyfikator — `worker_id` jedzie obok, bo po nim adresuje się mail.
    polaKonsumenta: [
      'task_id',
      'task_name',
      'title',
      'assignee',
      'worker_id',
      'task_description',
      'due_date',
    ],
    przykladowyLadunek: {
      assignment_id: '243642ee-5770-498f-aff8-41f46d139133',
      task_id: '00000000-0000-0000-0000-0000000004f1',
      task_name: 'Mieszanie: ZIARNISTA (partia WI-1A0A89B88AE)',
      title: 'Mieszanie: ZIARNISTA (partia WI-1A0A89B88AE)',
      assignee: 'RADOSŁAW JAROSŁAWSKI',
      worker_id: '1fa1a5ee-eca0-5a7d-8a2d-037ef501c26a',
      group_id: null,
      task_description:
        'HACCP: kontrola czasu i temperatury mieszania (CCP). Ciasto „ZIARNISTA”, temp. docelowa 25.00°C, czas 5 min.',
      task_module: 'waterandice',
      due_date: '2026-09-16T05:11:00.493Z',
      minutes_overdue: 4,
      level: 0,
    },
  },

  'provisio.order_delivered': {
    opis: 'Dostawa zamówienia → punkty Aegis (task_complete).',
    producenci: [
      { plik: 'apps/module-provisio/src/hooks/use-provisio.ts', token: "'order_delivered'" },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/nats/gamificationHandler.ts',
        token: "'provisio.order_delivered'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { order_id: '00000000-0000-0000-0000-00000000000f' },
  },

  'lexshift.leave_requested': {
    opis: 'Wniosek urlopowy → e-mail (pocztaZdarzen).',
    producenci: [
      { plik: 'apps/module-lexshift/src/hooks/use-lexshift.ts', token: "'leave_requested'" },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'lexshift.leave_requested'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { leave_id: '00000000-0000-0000-0000-000000000010' },
  },

  'lexshift.leave_approved': {
    opis: 'Urlop zatwierdzony → e-mail (pocztaZdarzen).',
    producenci: [
      { plik: 'apps/module-lexshift/src/hooks/use-lexshift.ts', token: "'leave_approved'" },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'lexshift.leave_approved'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { leave_id: '00000000-0000-0000-0000-000000000010' },
  },

  'artifex.policy_admitted': {
    opis: 'Polityka robota dopuszczona do katalogu → e-mail (pocztaZdarzen).',
    producenci: [{ plik: 'services/artifex-core/src/nats/events.ts', token: "'policy_admitted'" }],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'artifex.policy_admitted'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: { policy_id: '00000000-0000-0000-0000-000000000011' },
  },

  'artifex.consent_withdrawn': {
    opis:
      'Wycofanie zgody pracownika → e-mail. Wpis w mapie maili istnieje, ' +
      'producenta brak. Patrz ZNANE_ROZJAZDY.',
    producenci: [],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'artifex.consent_withdrawn'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: {},
  },

  'lexshift.ewidencja_niewyslana': {
    opis:
      'Dozór wysyłki dokumentów kadrowych: po 5. dniu miesiąca rejestr ' +
      '`kadry_dokumenty_wyslane` (migracja 719) nadal nie ma zestawienia ze ' +
      'statusem CREATED za miesiąc poprzedni, przy włączonym zakresie ' +
      '`ewidencja`. E-mail do właściciela i administratorów przez ' +
      'pocztaZdarzen; tłumienie ZERO, bo powtórkami steruje nadawca ' +
      '(raz na dobę). Bez tego zdarzenia brak ewidencji u biura jest ' +
      'niewidoczny z definicji — no-op nie rzuca wyjątkiem, on przepuszcza.',
    producenci: [
      {
        plik: 'services/nexus-core/src/kadry/KadrySync.ts',
        token: "'lexshift.ewidencja_niewyslana'",
      },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'lexshift.ewidencja_niewyslana'",
      },
    ],
    polaKonsumenta: [
      'rok',
      'miesiac',
      'miesiac_nazwa',
      'system',
      'system_nazwa',
      'powod',
      'wyslane',
      'do_uzgodnienia',
    ],
    przykladowyLadunek: {
      rok: 2026,
      miesiac: 8,
      miesiac_nazwa: 'sierpień 2026',
      system: 'saldeo',
      system_nazwa: 'SaldeoSMART',
      powod: 'Dwóch pracowników nie ma jeszcze numeru w systemie biura.',
      wyslane: 0,
      do_uzgodnienia: 2,
    },
  },

  'nexus.budget_threshold_reached': {
    opis:
      'Próg budżetu AI → e-mail. Wpis w mapie maili istnieje, producenta ' +
      'brak. Patrz ZNANE_ROZJAZDY.',
    producenci: [],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'nexus.budget_threshold_reached'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: {},
  },

  'nexus.policy_violation': {
    opis:
      'Naruszenie polityki → e-mail. Wpis w mapie maili istnieje, producenta ' +
      'brak. Patrz ZNANE_ROZJAZDY.',
    producenci: [],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'nexus.policy_violation'",
      },
    ],
    polaKonsumenta: [],
    przykladowyLadunek: {},
  },

  'nexus.certyfikat_wygasa': {
    opis:
      'Certyfikat modułowy partnera wchodzi w próg 30 albo 7 dni do końca ' +
      'ważności (fala P7 planu 45, migracje 717/721). Po wygaśnięciu partner ' +
      'traci wejście do tego organu na koncie KLIENTA — własne środowisko ' +
      'zostaje. Tłumik NIE jest w konsumencie: wiersze wydaje i od razu ' +
      'oznacza partner_certyfikaty_do_przypomnienia(), więc jedno zdarzenie ' +
      'na próg. E-mail imienny przez pocztaZdarzen, in-app przez novu-bridge.',
    producenci: [
      {
        plik: 'services/nexus-core/src/edukacja/edukacjaDoreczenie.ts',
        token: "'nexus.certyfikat_wygasa'",
      },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'nexus.certyfikat_wygasa'",
      },
    ],
    polaKonsumenta: [
      'user_id',
      'user_name',
      'partner_name',
      'temat',
      'numer',
      'wazny_do_pl',
      'dni',
    ],
    przykladowyLadunek: {
      certyfikat_id: '9c1d2e3f-0000-4000-8000-000000000717',
      partner_id: '7d3c2b1a-0000-4000-8000-000000000465',
      user_id: 'a1b2c3d4-0000-4000-8000-000000000001',
      user_name: 'Jan Kowalski',
      partner_name: 'AI Solution sp. z o.o.',
      module_id: 'fabrica',
      temat: 'Fabrica — produkcja',
      numer: 'CERT-FABRICA-20260918-A1B2C3',
      wazny_do: '2027-09-18T10:00:00.000Z',
      wazny_do_pl: '18 września 2027',
      dni: 30,
      prog: 30,
    },
  },

  'nexus.edukacja_zaproszenie': {
    opis:
      'Harmonogram edukacji Nexusa zaprasza partnera / osobę z zespołu na ' +
      'rozmowę edukacyjną, egzamin ustny albo quiz (wiedza wchodzi do progów ' +
      'programu partnerskiego, migracja 648). E-mail imienny przez ' +
      'pocztaZdarzen (adresat po users.id), in-app przez novu-bridge ' +
      '(workflow edukacja-zaproszenie, targetUser).',
    producenci: [
      {
        plik: 'services/nexus-core/src/edukacja/edukacjaDoreczenie.ts',
        token: "'nexus.edukacja_zaproszenie'",
      },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'nexus.edukacja_zaproszenie'",
      },
      {
        plik: 'services/novu-bridge/src/workflows/WorkflowMappings.ts',
        token: "'nexus.edukacja_zaproszenie'",
      },
    ],
    polaKonsumenta: ['user_id', 'user_name', 'partner_name', 'temat', 'rodzaj', 'rodzaj_nazwa'],
    przykladowyLadunek: {
      sesja_id: '2f9a1f6e-0000-4000-8000-000000000651',
      partner_id: '7d3c2b1a-0000-4000-8000-000000000465',
      user_id: 'a1b2c3d4-0000-4000-8000-000000000001',
      user_name: 'Jan Kowalski',
      partner_name: 'AI Solution sp. z o.o.',
      temat: 'Moduły i wycena',
      rodzaj: 'rozmowa',
      rodzaj_nazwa: 'rozmowę edukacyjną',
    },
  },

  'nexus.szkolenie_zaproszenie': {
    opis:
      'Harmonogram edukacji Nexusa zaprasza osobę z zespołu partnerskiego do ' +
      'Akademii — szkolenie produktowe po całej platformie (migracja 652), ' +
      'które poprzedza egzaminy wiedzy. E-mail imienny przez pocztaZdarzen, ' +
      'in-app przez novu-bridge (workflow szkolenie-zaproszenie, targetUser).',
    producenci: [
      {
        plik: 'services/nexus-core/src/edukacja/edukacjaDoreczenie.ts',
        token: "'nexus.szkolenie_zaproszenie'",
      },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'nexus.szkolenie_zaproszenie'",
      },
      {
        plik: 'services/novu-bridge/src/workflows/WorkflowMappings.ts',
        token: "'nexus.szkolenie_zaproszenie'",
      },
    ],
    polaKonsumenta: ['user_id', 'user_name', 'partner_name', 'ukonczone', 'wszystkie'],
    przykladowyLadunek: {
      partner_id: '7d3c2b1a-0000-4000-8000-000000000465',
      user_id: 'a1b2c3d4-0000-4000-8000-000000000001',
      user_name: 'Jan Kowalski',
      partner_name: 'AI Solution sp. z o.o.',
      ukonczone: 3,
      wszystkie: 11,
    },
  },

  'platforma.kontrola_licencjonowania': {
    opis:
      'Dobowy raport kontroli licencjonowania (widok `kontrola_licencjonowania`) → ' +
      'e-mail do zarządu organizacji platformy (pocztaZdarzen). Publikowany WYŁĄCZNIE ' +
      'przy wierszach `blad`, na temat organizacji z `settings.is_platform_organization`, ' +
      'którą wskazuje raport bazy. Do 28.10.2026 jedynym śladem był ERROR w logu ' +
      'neurosense-core bez czytelnika (wpis rejestru długu zamknięty w etapie 4 planu 56).',
    producenci: [
      {
        plik: 'services/neurosense-core/src/services/kontrolaLicencjonowania.ts',
        token: 'ZDARZENIE_KONTROLI',
      },
    ],
    konsumenci: [
      {
        plik: 'services/neurosense-core/src/services/pocztaZdarzen/mapa.ts',
        token: "'platforma.kontrola_licencjonowania'",
      },
    ],
    polaKonsumenta: ['bledy', 'ostrzezenia', 'wiersze', 'occurred_at'],
    przykladowyLadunek: {
      bledy: 2,
      ostrzezenia: 39,
      wg_kontroli: { licencja_bez_klasy_urzadzenia: 1, subskrypcja_bez_pozycji_cennika: 1 },
      wiersze: [
        {
          kontrola: 'licencja_bez_klasy_urzadzenia',
          organization_id: '1250a623-0000-4000-8000-000000000001',
          pozycja: 'UNI-AAAA-BBBB-CCCC',
          szczegol: 'licencja Telefon (status active) nie ma device_class — nie ma jak jej wycenić',
        },
        {
          kontrola: 'subskrypcja_bez_pozycji_cennika',
          organization_id: '1250a623-0000-4000-8000-000000000002',
          pozycja: 'artifex',
          szczegol: 'subskrypcja active (starter) bez aktywnej pozycji w module_prices',
        },
      ],
      occurred_at: '2026-10-28T05:00:00.000Z',
    },
  },
};

/**
 * Styki zepsute dziś — zmierzone, nie założone. Klucz musi istnieć
 * w `KONTRAKTY_ZDARZEN`; `watek` wskazuje PR-a naprawy z planu ORGANIZM.
 * Dla wpisu z tej listy test NIE wymaga (jeszcze) producenta ani kompletu
 * pól w przykładowym ładunku — wymaga za to, żeby lista nie miała martwych
 * wpisów (naprawiłeś styk = zdejmij wyjątek).
 */
export const ZNANE_ROZJAZDY: Record<string, { watek: string; powod: string }> = {
  // Styki poniżej wykrył test kontraktowy przy pierwszym uruchomieniu
  // (2026-08-19): wpis w mapie maili pocztaZdarzen istnieje, producenta nie ma
  // w całym repo — mail nigdy nie wychodzi. Naprawa nie jest przypisana do
  // żadnego wątku planu ORGANIZM (`watek: 'poza-planem'`) — to robota dla
  // właściciela organu-emitenta, zgłoszona jawnie zamiast udawać, że działa.
  'agenda.task_assigned': {
    watek: 'poza-planem',
    powod:
      'Mapa maili konsumuje; `buildAssignedEvent` z agenda-state-events.ts nie ' +
      'jest wołany w żadnym miejscu repozytorium. Publikator szablonowy ' +
      'AgendaEngine.ts wysyła wyłącznie nazwy z `AgendaEventName` ' +
      '(reminder / at_risk / overdue / escalation), a `task_assigned` należy do ' +
      '`AgendaStateEventName` — zmierzone 19.09.2026. Mapa jest już gotowa: ' +
      'czyta `assignee` i `title`, więc naprawa to samo wywołanie producenta.',
  },
  'artifex.consent_withdrawn': {
    watek: 'poza-planem',
    powod:
      'Mapa maili konsumuje, artifex-core nie emituje consent_withdrawn ' +
      '(events.ts ma tylko policy_admitted i rodzeństwo).',
  },
  'nexus.budget_threshold_reached': {
    watek: 'poza-planem',
    powod: 'Mapa maili konsumuje, nexus-core nigdzie nie publikuje tego zdarzenia.',
  },
  'nexus.policy_violation': {
    watek: 'poza-planem',
    powod: 'Mapa maili konsumuje, nexus-core nigdzie nie publikuje tego zdarzenia.',
  },
};

// =============================================================================
// MAGISTRALA ZAMÓWIEŃ — KONTRAKTY KOMEND I SZKIELETY ZDARZEŃ (PR 1.8)
// =============================================================================
//
// ARCH §11.1: „szkielety z pustymi listami producentów i konsumentów w PR 1.8
// (test wymaga dowodu w kodzie dla każdego wpisu), komplet rdzenia z polityką
// pamięci i dowodami w PR 2.4; zawsze przed pierwszą emisją”. Listy są puste,
// bo kodu jeszcze nie ma — pierwszy producent (drainer) powstaje w PR 2.3.
// Pusta lista jest tu PRAWDĄ („nikt nie emituje”), nie wartością zastępczą.
// =============================================================================

/**
 * Kontrakt komendy `commands.organ.{organ}.{akcja}` (`02` §5, §8).
 *
 * `terminOdpowiedziMs` i `zdarzeniaOdpowiedzi` mają w szkielecie wartość
 * „nieustalone” (`null`, `[]`): dokumenty nie podają liczb, a zmyślony termin
 * brzmiałby jak dana i uruchomiłby ponowienia `:p{N}` (§8) na podstawie
 * niczego. Ustala je PR 2.4 (wiersz „kontrakty + `KONTRAKTY_KOMEND`”), którego
 * bramka wymaga `termin_odpowiedzi` przy każdej komendzie (ARCH §6.6).
 */
export interface KontraktKomendy {
  /** Po ludzku: co komenda zleca i komu. */
  opis: string;
  /** Trwały konsument `ORGAN_COMMANDS` — brany z `KONSUMENCI_KOMEND`, nie przepisany. */
  konsument: string;
  /** Producenci — dowody publikacji (rdzeń, `bus_wstaw`). Pusta lista = nikt nie emituje. */
  producenci: DowodKontraktu[];
  /** Konsumenci — dowody obsługi w organie. */
  konsumenci: DowodKontraktu[];
  /**
   * Termin odpowiedzi liczony od `bus_outbox.opublikowano_at` (`02` §7.3
   * pkt 4); po nim ponowienie `:p{N}`, po dwóch alert (§8). `null` = jeszcze
   * nieustalony (szkielet).
   */
  terminOdpowiedziMs: number | null;
  /** Zdarzenia rdzenia (`{rodzina}.{nazwa}` ze słownika), którymi wraca odpowiedź. Pusta = nieustalone. */
  zdarzeniaOdpowiedzi: KluczZdarzeniaMagistrali[];
}

function szkieletKomendy(organ: OrganKomend, opis: string): KontraktKomendy {
  return {
    opis,
    konsument: KONSUMENCI_KOMEND[organ].nazwa,
    producenci: [],
    konsumenci: [],
    terminOdpowiedziMs: null,
    zdarzeniaOdpowiedzi: [],
  };
}

/**
 * Kontrakty komend organów — klucz `{organ}.{akcja}` (segmenty 5 i 6
 * tematu). Typ `Record<KluczKomendy, …>` sprawia, że brak albo nadmiar
 * komendy wobec `KOMENDY_ORGANOW` jest błędem kompilacji.
 */
export const KONTRAKTY_KOMEND: Record<KluczKomendy, KontraktKomendy> = {
  'mandatum.fulfillment_request': szkieletKomendy(
    'mandatum',
    'Zlecenie realizacji (FO) dla węzła z organem Mandatum (kompletacja, transport własny) → ' +
      'mandatum-core-zlecenia; organ odpowiada RPC `orders_fo_*` w swojej transakcji (ARCH §4.2).'
  ),
  'mandatum.fulfillment_cancel_request': szkieletKomendy(
    'mandatum',
    'Prośba o anulowanie FO przed `skompletowane` → mandatum-core-zlecenia (ARCH §4.2).'
  ),
  'mandatum.fulfillment_change_request': szkieletKomendy(
    'mandatum',
    'Zmiana linii FO `skompletowane` przed `wydane`, z wersją oczekiwaną (reżim 3a, ARCH §4.7) → ' +
      'mandatum-core-zlecenia; organ odpowiada `orders_fo_zmien_ilosc`.'
  ),
  'packflow.fulfillment_request': szkieletKomendy(
    'packflow',
    'Zlecenie realizacji (FO) dla węzła wysyłkowego (PackFlow + UCS; 3PL w temacie wykonawcy, ' +
      '`02` §9 izolacja (1)) → packflow-core-zlecenia.'
  ),
  'packflow.fulfillment_cancel_request': szkieletKomendy(
    'packflow',
    'Prośba o anulowanie FO przed wydaniem (ze `spakowane` po `przesylka.anuluj`) → packflow-core-zlecenia.'
  ),
  'packflow.fulfillment_change_request': szkieletKomendy(
    'packflow',
    'Zmiana linii FO `skompletowane`/`spakowane` przed `wydane` (reżim 3a, ARCH §4.7) → ' +
      'packflow-core-zlecenia.'
  ),
  'fabrica.demand_submit': szkieletKomendy(
    'fabrica',
    'Paczka zapotrzebowania zamrożona → zlecenie produkcyjne Fabriki (`source_system=orders-core`, ' +
      '`source_id=<batch_id>:v<N>`, `uq_fabrica_production_orders_zrodlo`) → ' +
      'fabrica-core-zapotrzebowanie; postęp przez `orders_produkcja_postep`.'
  ),
  'fabrica.demand_change': szkieletKomendy(
    'fabrica',
    'Zmiana zamrożonej paczki (delta wersji vN+1, ARCH §4.4) → fabrica-core-zapotrzebowanie.'
  ),
  'fabrica.demand_cancel': szkieletKomendy(
    'fabrica',
    'Wycofanie paczki zapotrzebowania → fabrica-core-zapotrzebowanie.'
  ),
  'fabrica.demand_plan': szkieletKomendy(
    'fabrica',
    'Planowanie paczki zapotrzebowania w Fabrice → fabrica-core-zapotrzebowanie (`02` §5).'
  ),
  'receptor.demand_submit': szkieletKomendy(
    'receptor',
    'Paczka zapotrzebowania → zadanie agenta Receptora (`receptor_agent_tasks`, ' +
      '`uq_receptor_agent_tasks_zrodlo`) → receptor-core-zapotrzebowanie.'
  ),
  'receptor.demand_cancel': szkieletKomendy(
    'receptor',
    'Wycofanie paczki u Receptora (`zlecenie.wycofaj`) → receptor-core-zapotrzebowanie.'
  ),
  'arkusz_produkcyjny.demand_submit': szkieletKomendy(
    'arkusz_produkcyjny',
    'Paczka zapotrzebowania → arkusz produkcyjny do druku (`client_job_key`) → print-core-arkusz.'
  ),
  'provisio.plan_supply': szkieletKomendy(
    'provisio',
    'Zaopatrzenie pod zamrożone zapotrzebowanie (MRP) → provisio-plan-komendy w nexus-core.'
  ),
  'provisio.batch_block': szkieletKomendy(
    'provisio',
    'Blokada partii (reklamacja, wycofanie) → provisio-plan-komendy; u 3PL w temacie opiekuna ' +
      'partii (`02` §9 izolacja (1)).'
  ),
  'integracje.intent_ready': szkieletKomendy(
    'integracje',
    'Dzwonek: intencja zapisu do systemu zewnętrznego gotowa (system w ładunku jako ' +
      '`connector_id`, UNIQUE `integration_intents`) → integracje-intencje; potwierdzana po ' +
      'rejestracji w bazie, limit dostawcy nigdy przez `nak` (`02` §6).'
  ),
  'kanal.fulfillment_confirm': szkieletKomendy(
    'kanal',
    'Potwierdzenie realizacji w kanale sprzedaży (adapter Shopify portu kanału, jedyny pisarz ' +
      'realizacji w sklepie do decyzji Z-5) → integracje-kanal.'
  ),
  'kanal.cancel_confirm': szkieletKomendy(
    'kanal',
    'Potwierdzenie anulowania w kanale sprzedaży → integracje-kanal.'
  ),
};

/** Szkielet kontraktu zdarzenia: tylko opis, wszystkie listy puste (ARCH §11.1). */
function szkieletZdarzenia(opis: string): KontraktZdarzenia {
  return { opis, producenci: [], konsumenci: [], polaKonsumenta: [], przykladowyLadunek: {} };
}

/**
 * Szkielety kontraktów zdarzeń rodzin outboxu (`02` §5) — OSOBNO od
 * `KONTRAKTY_ZDARZEN`, bo tamten słownik znaczy „żywy styk”: czytają go
 * dyspozytorzy w `kontrakt-zdarzen.test.ts`, `mapaAutonomii.test.ts`
 * i tytuły zdarzeń w powłoce (`tytulZKontraktu`). Szkielet z pustymi
 * listami w tamtym słowniku pozwoliłby dopisać konsumenta zdarzenia bez
 * producenta przy zielonych bramkach.
 *
 * PR 2.4 PRZENOSI wpis stąd do `KONTRAKTY_ZDARZEN` z dowodami, polami
 * konsumenta, prawdziwym ładunkiem i polityką pamięci (§4.2). Bramka
 * (`kontrakt-zdarzen.test.ts`) pilnuje, że każde zdarzenie słownika ma
 * dokładnie jeden kontrakt — tu albo tam.
 */
export const SZKIELETY_ZDARZEN: Partial<Record<KluczZdarzeniaMagistrali, KontraktZdarzenia>> = {
  // --- orders (orders-core) ---------------------------------------------------
  'orders.order_received': szkieletZdarzenia(
    'Zamówienie do przeglądu (`stan_przyjecia` `sparsowane` albo `do_przegladu`) → ekrany, enkoder.'
  ),
  'orders.order_accepted': szkieletZdarzenia(
    'Zamówienie przyjęte (`stan_przyjecia = przyjete`) → ekrany, enkoder (pamięć epizodyczna).'
  ),
  'orders.order_rejected': szkieletZdarzenia(
    'Zamówienie odrzucone (`orders_odrzuc`) → ekrany, enkoder, orders-core-miedzynajemcowy.'
  ),
  'orders.order_merged': szkieletZdarzenia(
    'Duplikat scalony z innym zamówieniem (`orders_scal`) → ekrany, enkoder.'
  ),
  'orders.order_confirmed': szkieletZdarzenia(
    'Zamówienie potwierdzone (`stan_handlowy = potwierdzone`) → ekrany, enkoder.'
  ),
  'orders.order_changed': szkieletZdarzenia(
    'Zestaw zmian zamówienia z nową wersją → ekrany, enkoder.'
  ),
  'orders.order_held': szkieletZdarzenia(
    'Zamówienie wstrzymane (`customer_order_holds`) → ekrany, enkoder, mobile-push-zamowienia.'
  ),
  'orders.order_released': szkieletZdarzenia('Wstrzymanie zamówienia zdjęte → ekrany, enkoder.'),
  'orders.order_cancelled': szkieletZdarzenia(
    'Zamówienie anulowane → ekrany, enkoder, orders-core-miedzynajemcowy.'
  ),
  'orders.order_closed': szkieletZdarzenia(
    'Zamówienie zamknięte (stan pochodny `zamkniete`, ARCH §4.1) → ekrany, enkoder.'
  ),
  'orders.promise_date_changed': szkieletZdarzenia(
    'Zmiana terminu obiecanego (ARCH §4.6) → poczta-zamowienia, mobile-push-zamowienia, ekrany.'
  ),
  'orders.fulfillment_order_created': szkieletZdarzenia(
    'Zlecenie realizacji (FO) utworzone przez router → ekrany, orders-core-miedzynajemcowy.'
  ),
  'orders.fulfillment_order_accepted': szkieletZdarzenia(
    'FO przyjęte przez węzeł → ekrany, orders-core-miedzynajemcowy.'
  ),
  'orders.fulfillment_order_rejected': szkieletZdarzenia(
    'FO odrzucone przez węzeł (dalej `do_ponownego_routingu`) → ekrany, ' +
      'orders-core-miedzynajemcowy, mobile-push-zamowienia.'
  ),
  'orders.fulfillment_order_progressed': szkieletZdarzenia(
    'Postęp FO (zaplanowane, w realizacji, skompletowane, spakowane) → ekrany, ' +
      'orders-core-miedzynajemcowy.'
  ),
  'orders.fulfillment_order_shorted': szkieletZdarzenia(
    'Braki w kompletacji FO → ekrany, orders-core-miedzynajemcowy, mobile-push-zamowienia.'
  ),
  'orders.fulfillment_order_dispatched': szkieletZdarzenia(
    'FO wydane (fakt fizyczny: załadunek, skan przekazania kurierowi) → ekrany, ' +
      'orders-core-miedzynajemcowy.'
  ),
  'orders.fulfillment_order_delivered': szkieletZdarzenia(
    'FO dostarczone (POD organu albo tracking UCS) → ekrany, enkoder, orders-core-miedzynajemcowy.'
  ),
  'orders.fulfillment_order_failed': szkieletZdarzenia(
    'FO niedostarczone → ekrany, orders-core-miedzynajemcowy, poczta-zamowienia.'
  ),
  'orders.fulfillment_order_cancelled': szkieletZdarzenia(
    'FO anulowane → ekrany, orders-core-miedzynajemcowy.'
  ),
  'orders.production_demand_frozen': szkieletZdarzenia(
    'Zapotrzebowanie zamrożone po cut-off (wersja vN, ARCH §4.4) → provisio-plan-zdarzenia, ekrany.'
  ),
  'orders.production_demand_status_changed': szkieletZdarzenia(
    'Zmiana stanu paczki zapotrzebowania (wysłana, przyjęta, w produkcji, wykonana) → ' +
      'provisio-plan-zdarzenia, ekrany.'
  ),
  'orders.production_change_requested': szkieletZdarzenia(
    'Prośba o zmianę po cut-off (`production_change_requests`) → mobile-push-zamowienia, ekrany.'
  ),
  'orders.adjustment_registered': szkieletZdarzenia(
    'Korekta po wydaniu zarejestrowana (`order_adjustments`: zwrot, niedostawa, korekta ilości, ' +
      'reklamacja partii) → ekrany.'
  ),
  'orders.batch_recall_opened': szkieletZdarzenia(
    'Wycofanie partii otwarte → poczta-zamowienia, ekrany, enkoder.'
  ),
  'orders.partner_order_sent': szkieletZdarzenia(
    'Zamówienie wysłane do partnera (dropshipping, `orders_partner_zlec`) → ekrany, przekaźnik.'
  ),
  // --- documents (orders-core, po wyniku adaptera) ----------------------------
  'documents.document_requested': szkieletZdarzenia(
    'Żądanie dokumentu handlowego założone przez rdzeń → ekrany.'
  ),
  'documents.document_issued': szkieletZdarzenia(
    'Dokument wystawiony przez system prowadzący (numer z adaptera) → ekrany, enkoder.'
  ),
  'documents.document_fiscalized': szkieletZdarzenia('Dokument z numerem KSeF → ekrany.'),
  'documents.document_rejected': szkieletZdarzenia(
    'Dokument odrzucony przez system albo KSeF → poczta-zamowienia, ekrany, enkoder.'
  ),
  'documents.document_unknown': szkieletZdarzenia(
    'Wynik zapisu dokumentu nieznany (uzgodnienie po znaczniku, ARCH §4.5) → ekrany.'
  ),
  'documents.payment_matched': szkieletZdarzenia(
    'Płatność dopasowana do dokumentu (lustro FK) → ekrany.'
  ),
  'documents.document_registered': szkieletZdarzenia(
    'Dokument zarejestrowany (`dokumenty_zarejestruj`, D53-4) → ekrany, mail `wymaga_decyzji` ' +
      '(plan 53, UD-12).'
  ),
  // --- integracje (orders-core, alerty) ---------------------------------------
  'integracje.intent_failed': szkieletZdarzenia(
    'Alert: intencja zapisu do systemu zewnętrznego odrzucona → poczta-zamowienia.'
  ),
  'integracje.agent_overdue': szkieletZdarzenia(
    'Alert: agent lokalny (ERP, Receptor) nie odpowiedział w terminie → poczta-zamowienia.'
  ),
  'integracje.parity_mismatch': szkieletZdarzenia(
    'Alert: rozbieżność parzystości Universe ↔ FK (dobowo, także `nr_ksef`) → poczta-zamowienia.'
  ),
  'integracje.budget_breached': szkieletZdarzenia(
    'Alert: przekroczony budżet opóźnień ścieżki (3 okna 15 min, `02` §10) → poczta-zamowienia.'
  ),
  // --- mandatum (mandatum-core, outbox) ---------------------------------------
  'mandatum.wave_completed': szkieletZdarzenia('Fala kompletacji zakończona → TV, enkoder.'),
  'mandatum.pick_confirmed': szkieletZdarzenia(
    'Pozycja skompletowana (pojedynczy skan: fala, pozycja, ilość, brak) → TV ≤ 2 s, enkoder.'
  ),
  'mandatum.short_pick': szkieletZdarzenia('Brak przy kompletacji pozycji → TV, enkoder.'),
  'mandatum.loading_completed': szkieletZdarzenia('Załadunek zakończony → TV, enkoder.'),
  'mandatum.crate_loaded': szkieletZdarzenia(
    'Skrzynka załadowana (sesja, skrzynka, strefa) → TV, enkoder.'
  ),
  'mandatum.crate_missing': szkieletZdarzenia('Brak skrzynki przy załadunku → TV, enkoder.'),
  'mandatum.crate_damaged': szkieletZdarzenia('Skrzynka uszkodzona → TV, enkoder.'),
  'mandatum.route_run_started': szkieletZdarzenia('Kurs trasy rozpoczęty → TV, enkoder.'),
  'mandatum.route_run_completed': szkieletZdarzenia(
    'Kurs trasy zakończony → TV, enkoder (węzeł `mandatum.route` z `encje` producenta, `02` §4.1).'
  ),
  'mandatum.stop_delivered': szkieletZdarzenia('Przystanek obsłużony → TV, enkoder.'),
  'mandatum.stop_problem': szkieletZdarzenia(
    'Problem na przystanku → poczta-zamowienia, TV, enkoder.'
  ),
  'mandatum.container_moved': szkieletZdarzenia('Ruch pojemnika → TV, enkoder.'),
  'mandatum.adjustment_received': szkieletZdarzenia(
    'Korekta odebrana przez organ (`mandatum_adjustment_receipts`) → TV, enkoder.'
  ),
  'mandatum.intake_overdue': szkieletZdarzenia(
    'Alert: tętno portu `poczta_wejsciowa` starsze niż 5 min w godzinach pracy → poczta-zamowienia.'
  ),
  // --- packflow (packflow-core, outbox) ---------------------------------------
  'packflow.packing_started': szkieletZdarzenia(
    'Pakowanie FO rozpoczęte (sesja w bazie) → ekrany, enkoder.'
  ),
  'packflow.package_packed': szkieletZdarzenia(
    'Paczka spakowana (etykieta kupiona, paczka na stole) → ekrany, enkoder.'
  ),
  // --- provisio (Provisio, outbox) --------------------------------------------
  'provisio.reservation_created': szkieletZdarzenia(
    'Rezerwacja wyrobu albo surowca utworzona (`provisio_reservations`) → ekrany.'
  ),
  'provisio.reservation_released': szkieletZdarzenia(
    'Rezerwacja zwolniona albo wygasła po `wazna_do` → ekrany.'
  ),
  'provisio.material_shortage_detected': szkieletZdarzenia(
    'Brak surowca wykryty → poczta-zamowienia, mobile-push-zamowienia, ekrany.'
  ),
  'provisio.purchase_proposal_created': szkieletZdarzenia(
    'Propozycja zakupu utworzona (`provisio_purchase_proposals`) → ekrany.'
  ),
  'provisio.order_overdue': szkieletZdarzenia(
    'Zamówienie zakupu po terminie dostawy (`opozniona_od`) → poczta-zamowienia, ekrany.'
  ),
  'provisio.production_received': szkieletZdarzenia(
    'Przyjęcie wyrobu z produkcji na stan → ekrany.'
  ),
};

/**
 * Ładunek okna czasowego (Strażnik Okien — W7). Emisja tylko przy przejściu
 * werdyktu; `zrodlo_normy` niesie cytat z `normyPiekarskie.ts`, bo norma jest
 * uwagą, nigdy odmową.
 */
export interface OknoCzasowePayload {
  krawedz_id: string;
  poprzednik_stage_id: string;
  nastepnik_stage_id: string;
  production_order_line_id: string | null;
  werdykt: 'zamyka_sie' | 'przekroczone' | 'zapas';
  pozostalo_minut: number | null;
  okno_minut: number | null;
  zrodlo_normy: string | null;
}

/** Ładunek decyzji ze Skrzynki Dowódcy (W6) — referencja, nigdy kopia sprawy. */
export interface SprawaDecyzjaPayload {
  sprawa_id: string;
  rodzaj:
    | 'inicjatywa'
    | 'okazja_steering'
    | 'remediacja'
    | 'analiza_produktu'
    | 'okno_czasowe'
    | 'partia_haccp'
    | 'obsada_grafiku';
  zrodlo_ref: string;
  decyzja: 'zatwierdzona' | 'odrzucona';
  uzasadnienie: string;
}
